import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress
} from '@material-ui/core'
import { Delete } from '@material-ui/icons'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined'
import GroupIcon from '@material-ui/icons/Group'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LocalAtmOutlinedIcon from '@material-ui/icons/LocalAtmOutlined'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import PolicyIcon from '@material-ui/icons/Policy'
import parse from 'html-react-parser'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import Lightbox from 'yet-another-react-lightbox'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/plugins/thumbnails.css'

import NoPictureDeafault from '../../../../assets/images/NoPictureDeafault.png'
import insertPhoto from '../../../../assets/images/insertPhoto.svg'
import CustomCounter from '../../../../components/CustomCounter'
import CustomTooltip from '../../../../components/CustomTooltip.tsx'
import {
  REMOVE_ITEM_FROM_CART_MUTATION,
  UPDATE_ITEM_QUANTITY_IN_CART_MUTATION
} from '../../../../graphql/cartCrud'
import { formatShortDateWithWeekDay } from '../../../../helpers/date'
import { errorNoCartFound, extractError } from '../../../../helpers/regex'
import { stripHtmlTags } from '../../../../helpers/stripHTML'
import { cleanCartData } from '../../../../helpers/userData'
import { getTranslate, lang } from '../../../../localization'
import { CartItem, CartResponse } from '../../../../types/cart'
import SimpleFacility from '../../../BookingHotelSearchResult/components/Facility'
import AddOnRow from '../AddOnRow'
import {
  getCancelPolicyContent,
  getModifyPolicyContent,
  ToolTipContentForCancelPolicy,
  ToolTipContentForModifyPolicy
} from './PolicyText'
import { useStyles } from './styles/index.styles'

type SelectedRoomCardProps = {
  cartItem: CartItem | undefined
  cartID: string
  clickedItemHash: string
  setClickedItemHash: React.Dispatch<React.SetStateAction<string>>
  refetchCart: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      cartResponse: CartResponse
    }>
  >
  loadingForGetCart: boolean
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
  setRedirectTitle: React.Dispatch<React.SetStateAction<string>>
}
export default function SelectedRoomCard({
  cartItem,
  cartID,
  clickedItemHash,
  setClickedItemHash,
  refetchCart,
  loadingForGetCart,
  setShowModal,
  setRedirectTitle
}: SelectedRoomCardProps) {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const facilities = cartItem?.roomType.facilities || []
  const visibleFacilities = facilities.slice(0, 20)
  const [showAllFacility, setShowAllFacility] = useState(false)
  const remainingFacilities = facilities.slice(20)
  const [itemQuantity, setItemQuantity] = useState(cartItem?.quantity || 0)
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>(
    cartItem?.selectedAddOnes || []
  )
  const [openGallary, setOpenGallary] = useState(false)
  const slides = cartItem?.roomType.images.map((img) => ({
    src: img
  }))
  useEffect(() => {
    setItemQuantity(cartItem?.quantity!)
  }, [cartItem?.quantity])

  const handleRefetch = async () => {
    const scrollY = window.scrollY
    await refetchCart()
    window.scrollTo({ top: scrollY, behavior: 'auto' })
  }

  //DELETE_ITEM_MUTATION
  const [deleteItem, { loading: loadingForDeleteItem }] = useMutation(
    REMOVE_ITEM_FROM_CART_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: () => {
        // toast.success(res.message)

        handleRefetch()
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  //UPDATE_ITEM_QUANTITY
  const [updateItemQuantity, { loading: loadingForUpdate }] = useMutation(
    UPDATE_ITEM_QUANTITY_IN_CART_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        toast.success(res.message)
        handleRefetch()
      },
      onError: (err) => {
        if (extractError(err.cause?.extensions) === errorNoCartFound) {
          setShowModal(true)
          setRedirectTitle('Your cart has expired.')
          cleanCartData()
          localStorage.removeItem('confirmPrimitiveInput')
          localStorage.removeItem('confirmPrimitiveReservationState')
          localStorage.removeItem('hotelDetails')
          localStorage.removeItem('primitiveData')
        } else {
          toast.error(getTranslate('Something went wrong'))
        }
      }
    }
  )

  const prevQuantity = useRef(itemQuantity)
  useEffect(() => {
    if (prevQuantity.current === itemQuantity) return
    prevQuantity.current = itemQuantity
    updateItemQuantity({
      variables: {
        input: { cartID: cartID, itemID: cartItem?.id, quantity: itemQuantity }
      }
    })
  }, [itemQuantity])

  const modifyContent = getModifyPolicyContent(cartItem)
  const cancelContent = getCancelPolicyContent(cartItem)

  return (
    <>
      <div className={classes.card}>
        <div className={classes.iconContainer}>
          <IconButton
            classes={{ root: classes.iconPad }}
            className={classes.deleteButton}
            disabled={loadingForDeleteItem}
            onClick={() => {
              deleteItem({
                variables: {
                  input: {
                    cartID: cartID,
                    itemID: cartItem?.id
                  }
                }
              })
            }}>
            {loadingForDeleteItem ? (
              <CircularProgress
                className={classes.circularProgress}
                size={16}
              />
            ) : (
              <Delete />
            )}
          </IconButton>
        </div>
        <div className={classes.header}>
          <div className={classes.imageContainer}>
            <img
              src={
                cartItem?.roomType?.images &&
                cartItem.roomType.images.length > 0
                  ? cartItem.roomType.images[0]
                  : NoPictureDeafault
              }
              alt="Room"
              className={classes.roomImage}
            />
            {slides && slides.length > 1 && (
              <div
                className={classes.counter}
                onClick={() => setOpenGallary(true)}>
                <img src={insertPhoto} className={classes.photoIcon} />
                <Typography variant="h5" className={classes.iconText}>
                  {`+${slides.length - 1}`}
                </Typography>
              </div>
            )}
          </div>
          <div className={classes.roomName}>
            <div>
              <Typography
                variant="h5"
                gutterBottom
                className={classes.verticalAlign}>
                {!isMobile && <MeetingRoomIcon className={classes.icon} />}
                {isMobile
                  ? `${cartItem?.roomType.name}`
                  : `${cartItem?.roomType.name} (${cartItem?.roomType.displayCode})`}
              </Typography>
              <div className={classes.iconTextRow}>
                {!isMobile && (
                  <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    className={classes.greyText}
                    gutterBottom>
                    {isMobile
                      ? `${cartItem?.ratePlan.name} (${cartItem?.ratePlan.displayCode})`
                      : `${cartItem?.ratePlan.name} (${cartItem?.ratePlan.displayCode})  | `}
                  </Typography>
                )}
                <div
                  className={[
                    classes.iconTextRow,
                    isMobile && classes.iconTextRowSmall
                  ].join(' ')}>
                  <GroupIcon className={classes.smallIcon} fontSize="small" />
                  <Typography variant="h5" className={classes.textafterIcon}>
                    {`${cartItem?.occupancy.adults} ${getTranslate('Adults')}`}
                    {cartItem?.occupancy.extraBeds! > 0 &&
                      ` ${getTranslate('and')} ${
                        cartItem?.occupancy.extraBeds
                      } ${getTranslate('Extra bed')}`}
                  </Typography>
                </div>
              </div>
            </div>
            {!isMobile ? (
              <div className={classes.headerBottom}>
                <div>
                  <Typography variant="h5" className={classes.boardTypeText}>
                    {`${getTranslate('Board Type')}: 
                       ${getTranslate(cartItem?.ratePlan.boardType || '')}
                    `}
                  </Typography>
                  <div className={classes.rateContainer}>
                    {cancelContent ? (
                      <CustomTooltip
                        title={
                          <ToolTipContentForCancelPolicy cartItem={cartItem} />
                        }
                        placement="top"
                        arrow
                        classes={{
                          tooltip: classes.toolTip,
                          arrow: classes.arrow
                        }}>
                        <div className={classes.ratePolicy}>
                          <PolicyIcon className={classes.redIcon} />
                          <Typography variant="h5" className={classes.redtext}>
                            {getTranslate('Cancellation Policy')}
                          </Typography>
                          <InfoOutlinedIcon className={classes.infoIcon} />
                        </div>
                      </CustomTooltip>
                    ) : (
                      <div className={classes.ratePolicy}>
                        <CheckCircleIcon className={classes.greenIcon} />
                        <Typography
                          variant={isMobile ? 'h6' : 'h5'}
                          className={classes.greenIconText}>
                          {getTranslate('Free Cancellation')}
                        </Typography>
                      </div>
                    )}
                    {modifyContent && modifyContent.length > 0 ? (
                      <CustomTooltip
                        title={
                          <ToolTipContentForModifyPolicy cartItem={cartItem} />
                        }
                        placement="top"
                        arrow
                        classes={{
                          tooltip: classes.toolTip,
                          arrow: classes.arrow
                        }}>
                        <div className={classes.ratePolicy}>
                          <CreateOutlinedIcon className={classes.yellowIcon} />
                          <Typography
                            variant={isMobile ? 'h6' : 'h5'}
                            className={classes.yellowText}>
                            {getTranslate('Modify Policy')}
                          </Typography>
                          <InfoOutlinedIcon className={classes.infoIcon} />
                        </div>
                      </CustomTooltip>
                    ) : (
                      <div className={classes.ratePolicy}>
                        <CheckCircleIcon className={classes.greenIcon} />
                        <Typography
                          variant={isMobile ? 'h6' : 'h5'}
                          className={classes.greenIconText}>
                          {getTranslate('Free Modification')}
                        </Typography>
                      </div>
                    )}
                    {cartItem && cartItem.ratePolicy.length > 2 && (
                      <CustomTooltip
                        title={
                          <div className={classes.main}>
                            <div className={classes.headerToolTip}>
                              <Typography
                                variant="h5"
                                className={classes.toolTipText}>
                                {getTranslate('Rate Policy')}
                              </Typography>
                            </div>
                            <div
                              className={classes.content}
                              style={{
                                direction: lang === 'fa' ? 'rtl' : 'ltr', // make layout direction dynamic
                                textAlign: lang === 'fa' ? 'right' : 'left', // align text accordingly
                                whiteSpace: 'pre-line', // keep newlines from stripHtmlTags
                                unicodeBidi: 'embed' // handle mixed RTL/LTR properly
                              }}>
                              {parse(
                                stripHtmlTags(cartItem.ratePolicy).replace(
                                  /\n/g,
                                  '<br/>'
                                )
                              )}
                            </div>
                          </div>
                        }
                        classes={{
                          tooltip: classes.toolTip,
                          arrow: classes.arrow
                        }}
                        placement="top"
                        arrow>
                        <div className={classes.ratePolicy}>
                          <LocalAtmOutlinedIcon className={classes.blueIcon} />
                          <Typography variant="h5" className={classes.blueText}>
                            {getTranslate('Rate Policy')}
                          </Typography>
                          <InfoOutlinedIcon className={classes.infoIcon} />
                        </div>
                      </CustomTooltip>
                    )}
                  </div>
                </div>
                <div className={classes.checkContainer}>
                  <div className={classes.check}>
                    <Typography variant="h5" className={classes.dateLabel}>
                      {`${getTranslate('Check-in')}: `}
                    </Typography>
                    <Typography variant="h5" className={classes.dateValue}>
                      {formatShortDateWithWeekDay(
                        cartItem?.arrival ? cartItem.arrival : '',
                        lang
                      )}
                    </Typography>
                  </div>
                  <div className={classes.check}>
                    <Typography variant="h5" className={classes.dateLabel}>
                      {`${getTranslate('Check-out')}: `}
                    </Typography>
                    <Typography variant="h5" className={classes.dateValue}>
                      {formatShortDateWithWeekDay(
                        cartItem?.departure ? cartItem.departure : '',
                        lang
                      )}
                    </Typography>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Button
                  variant="text"
                  size="small"
                  className={classes.btn}
                  onClick={() => setShowAllFacility(!showAllFacility)}>
                  {showAllFacility
                    ? getTranslate('Show Less Facilities')
                    : getTranslate('Show All Facilities')}
                </Button>

                {showAllFacility && (
                  <div className={classes.facilityContainer}>
                    {facilities.map((fac, i) => (
                      <SimpleFacility title={fac} key={i} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {isMobile && (
          <div className={classes.middle}>
            <Typography
              variant="h5"
              gutterBottom
              className={classes.verticalAlign}>
              <MeetingRoomIcon className={classes.icon} />
              {` ${cartItem?.ratePlan.name}`}
            </Typography>
            <Typography variant="h6" className={classes.boardTypeText}>
              {`${getTranslate('Board Type')}: ${getTranslate(
                cartItem?.ratePlan.boardType || ''
              )}`}
            </Typography>
            <div className={classes.rateContainer}>
              {cancelContent ? (
                <CustomTooltip
                  title={<ToolTipContentForCancelPolicy cartItem={cartItem} />}
                  placement="top"
                  arrow
                  classes={{
                    tooltip: classes.toolTip,
                    arrow: classes.arrow
                  }}>
                  <div className={classes.ratePolicy}>
                    <PolicyIcon className={classes.redIcon} />
                    <Typography variant="h6" className={classes.redtext}>
                      {getTranslate('Cancellation Policy')}
                    </Typography>
                    <InfoOutlinedIcon className={classes.infoIcon} />
                  </div>
                </CustomTooltip>
              ) : (
                <div className={classes.ratePolicy}>
                  <CheckCircleIcon className={classes.greenIcon} />
                  <Typography variant="h6" className={classes.greenIconText}>
                    {getTranslate('Free Cancellation')}
                  </Typography>
                </div>
              )}
              {modifyContent && modifyContent.length > 0 ? (
                <CustomTooltip
                  title={<ToolTipContentForModifyPolicy cartItem={cartItem} />}
                  placement="top"
                  arrow
                  classes={{
                    tooltip: classes.toolTip,
                    arrow: classes.arrow
                  }}>
                  <div className={classes.ratePolicy}>
                    <CreateOutlinedIcon className={classes.yellowIcon} />
                    <Typography variant="h6" className={classes.yellowText}>
                      {getTranslate('Modify Policy')}
                    </Typography>
                    <InfoOutlinedIcon className={classes.infoIcon} />
                  </div>
                </CustomTooltip>
              ) : (
                <div className={classes.ratePolicy}>
                  <CheckCircleIcon className={classes.greenIcon} />
                  <Typography variant="h6" className={classes.greenIconText}>
                    {getTranslate('Free Modification')}
                  </Typography>
                </div>
              )}
              {cartItem && cartItem.ratePolicy.length > 2 && (
                <CustomTooltip
                  title={
                    <div className={classes.main}>
                      <div className={classes.headerToolTip}>
                        <Typography
                          variant="h6"
                          className={classes.toolTipText}>
                          {getTranslate('Rate Policy')}
                        </Typography>
                      </div>
                      <div
                        className={classes.content}
                        style={{
                          direction: lang === 'fa' ? 'rtl' : 'ltr',
                          textAlign: lang === 'fa' ? 'right' : 'left',
                          whiteSpace: 'pre-line',
                          unicodeBidi: 'embed'
                        }}>
                        {parse(
                          stripHtmlTags(cartItem.ratePolicy).replace(
                            /\n/g,
                            '<br/>'
                          )
                        )}
                      </div>
                    </div>
                  }
                  classes={{
                    tooltip: classes.toolTip,
                    arrow: classes.arrow
                  }}
                  placement="top"
                  arrow>
                  <div className={classes.ratePolicy}>
                    <LocalAtmOutlinedIcon className={classes.blueIcon} />
                    <Typography variant="h6" className={classes.blueText}>
                      {getTranslate('Rate Policy')}
                    </Typography>
                    <InfoOutlinedIcon className={classes.infoIcon} />
                  </div>
                </CustomTooltip>
              )}
            </div>
            <div className={classes.middleBottom}>
              <div>
                <div className={classes.check}>
                  <Typography variant="h6" className={classes.dateLabel}>
                    {`${getTranslate('Check-in')}: `}
                  </Typography>
                  <Typography variant="h6" className={classes.dateValue}>
                    {formatShortDateWithWeekDay(
                      cartItem?.arrival ? cartItem.arrival : '',
                      lang
                    )}
                  </Typography>
                </div>
                <div className={classes.check}>
                  <Typography variant="h6" className={classes.dateLabel}>
                    {`${getTranslate('Check-out')}: `}
                  </Typography>
                  <Typography variant="h6" className={classes.dateValue}>
                    {formatShortDateWithWeekDay(
                      cartItem?.departure ? cartItem.departure : '',
                      lang
                    )}
                  </Typography>
                </div>
              </div>
              <div className={classes.counterContainer}>
                <div>
                  <CustomCounter
                    value={cartItem?.quantity!}
                    setValue={(val) => {
                      setClickedItemHash(cartItem?.hash!)
                      setItemQuantity(val)
                    }}
                    max={cartItem?.maxAvailibility}
                    disable={
                      clickedItemHash === cartItem?.hash &&
                      (loadingForUpdate || loadingForGetCart)
                    }
                    loading={
                      clickedItemHash === cartItem?.hash &&
                      (loadingForUpdate || loadingForGetCart)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={classes.facilityAndCounter}>
          <div className={classes.facilitySection}>
            {!isMobile && facilities.length > 0 && (
              <>
                <Typography variant="subtitle2" className={classes.sub1}>
                  {getTranslate('Facilities')}
                </Typography>
                <div className={classes.facilityList}>
                  {visibleFacilities.map((fac, i) => (
                    <SimpleFacility title={fac} key={i} />
                  ))}
                  {remainingFacilities.length > 0 && (
                    <Tooltip
                      title={
                        <div>
                          {remainingFacilities.map((fac, i) => (
                            <Typography key={i} variant="body2">
                              {getTranslate(fac)}
                            </Typography>
                          ))}
                        </div>
                      }
                      arrow>
                      <div>
                        <SimpleFacility
                          title={`+${remainingFacilities.length} more`}
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </>
            )}
          </div>
          {!isMobile && (
            <div className={classes.countContainer}>
              <CustomCounter
                value={cartItem?.quantity!}
                setValue={(val) => {
                  setClickedItemHash(cartItem?.hash!)
                  setItemQuantity(val)
                }}
                max={cartItem?.maxAvailibility}
                disable={
                  clickedItemHash === cartItem?.hash &&
                  (loadingForUpdate || loadingForGetCart)
                }
                loading={
                  clickedItemHash === cartItem?.hash &&
                  (loadingForUpdate || loadingForGetCart)
                }
              />
            </div>
          )}
        </div>
        {cartItem?.availableAddOns && cartItem.availableAddOns.length > 0 && (
          <>
            <div className={classes.addOnContainer}>
              <div className={classes.tableHeader}>
                <Typography variant="h5" className={classes.sub1}>
                  {getTranslate('Add-Ons')}
                </Typography>
                <Typography variant="h5" className={classes.sub}>
                  {getTranslate('Choice')}
                </Typography>
                <Typography variant="h5" className={classes.sub}>
                  {getTranslate('Add on price')}
                </Typography>
              </div>
              {cartItem?.availableAddOns.map((a, i) => (
                <AddOnRow
                  data={a}
                  selectedAddOns={selectedAddOns}
                  setSelectedAddOns={setSelectedAddOns}
                  cartID={cartID}
                  itemID={cartItem.id}
                  handleRefetch={handleRefetch}
                  key={i}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <Lightbox
        open={openGallary}
        close={() => setOpenGallary(false)}
        slides={slides}
        thumbnails={{
          position: 'bottom',
          width: 100,
          height: 100,
          imageFit: 'cover'
        }}
        plugins={[Thumbnails]}
        carousel={{
          finite: true
        }}
        styles={{
          container: {
            backgroundColor: 'black'
          }
        }}
      />
    </>
  )
}
